import { useCallback, useEffect } from 'react';

import {
  ACTIVITY_BUILDER_EVENT_SECTION_VIEW,
  pushActivityBuilderPresentViewEvent,
} from './activity-builder-present-data-tracking.utils';
import { ASSESSMENT, LESSON, LESSON_ACTIVITIES } from 'global/constants';
import { InquisitiveActivitiesContentStatus } from 'global/graphql/scalars.type';
import { SectionOrSubSectionOverview } from '../activity-builder.constants';
import { ExtraSectionIdentifiers } from '../activity-builder.apollo';
import { StudentSectionOrSubSectionOverview } from '../student/student.constants';
import { useActivityBuilderPresentContext } from './activity-builder-present.context';
import ActivityBuilderZoomToFitControl from '../zoom-to-fit/activity-builder-zoom-to-fit-control';

type Props = {
  activeSectionId: string;
  activityId: string | null;
  contentType: string;
  contentId: number;
  version?: string;
  status: InquisitiveActivitiesContentStatus;
  sectionIds: string[];
  currentSectionOverview:
    | SectionOrSubSectionOverview
    | StudentSectionOrSubSectionOverview
    | null;
  sectionsOverview:
    | SectionOrSubSectionOverview[]
    | StudentSectionOrSubSectionOverview[];
};

const ActivityBuilderZoomToFit = ({
  activeSectionId,
  activityId,
  contentType,
  contentId,
  version,
  status,
  sectionIds,
  currentSectionOverview,
  sectionsOverview,
}: Props) => {
  const { isZoomToFit, initialiseZoomToFit, setZoomToFit } =
    useActivityBuilderPresentContext();

  const handleClick = useCallback(
    (isZoom: boolean) => {
      if (isZoom === isZoomToFit) return;

      setZoomToFit(isZoom);
      pushActivityBuilderPresentViewEvent({
        activityId,
        eventType: ACTIVITY_BUILDER_EVENT_SECTION_VIEW,
        sectionId: activeSectionId,
        contentType: contentType === LESSON_ACTIVITIES ? LESSON : ASSESSMENT,
        contentId,
        version,
        status,
        index: sectionIds.indexOf(activeSectionId) + 1,
        isZoomToFit: isZoom,
        currentSectionOverview,
        sectionsOverview,
      });
    },
    [
      isZoomToFit,
      activeSectionId,
      activityId,
      contentId,
      contentType,
      currentSectionOverview,
      sectionIds,
      sectionsOverview,
      status,
      version,
      setZoomToFit,
    ]
  );

  useEffect(() => {
    initialiseZoomToFit();
    //eslint-disable-next-line
  }, []);

  if (activeSectionId === ExtraSectionIdentifiers.Introduction) {
    return null;
  }

  return (
    <ActivityBuilderZoomToFitControl
      aria-label={`Screen View Settings. Currently set to ${
        isZoomToFit ? 'Presentation' : 'Mobile'
      }`}
      isZoomToFit={isZoomToFit}
      onClick={handleClick}
    />
  );
};

export default ActivityBuilderZoomToFit;
